// eslint-disable-next-line import/no-deprecated
import {LineItemModel} from '../LineItems/LineItem.model';
import {CouponModel} from './Coupon.model';
import {PriceModel} from '../Price.model';
import {CartDiscount, DiscountRule, LineItem, MultiCurrencyPrice} from '@wix/auto_sdk_ecom_current-cart';
import {ModelContext, CartV1WithPrivateFields} from '../../../types/app.types';
import {getUniques} from '../../utils/getUniques';
import {SPECS} from '../../specs';
import {BusinessInfoModel} from './BusinessInfo.model';
import {CustomerInfoModel} from './CustomerInfo.model';
import {PaymentInfoModel} from './PaymentInfo.model';
import {DeliveryInfoModel} from './DeliveryInfo.model';
import {Cart} from '@wix/auto_sdk_ecom_current-cart-v-2';
import {TaxInfoModel} from './TaxInfo.model';

type CartDiscountWithPrivateFields = CartDiscount & {discountRule?: DiscountRule; lineItemIds: string[]};
export type LineItemWithPrivateFields = LineItem & {
  lineItemPrice: MultiCurrencyPrice;
};

export class CartModel {
  public id: string;
  public revision?: string;
  public lineItems: LineItemModel[];
  public subtotal: PriceModel;
  public note: string;
  public businessInfo: BusinessInfoModel;
  public customerInfo: CustomerInfoModel;
  public paymentInfo: PaymentInfoModel;
  public purchaseFlowId: string;
  public deliveryInfo: DeliveryInfoModel;
  public taxInfo: TaxInfoModel;
  public hasFormsOnCheckout?: boolean;

  public lineItemsTypes: string[];
  public hasSubscriptionItem: boolean;
  public hasItemWithFreeTrial: boolean;
  public hasLineItemWithCouponDiscount?: boolean;

  public coupon?: CouponModel;

  /**
   * @deprecated
   */
  public checkoutId?: string;

  constructor(params: CartModel) {
    this.id = params.id;
    this.revision = params.revision;
    this.lineItems = params.lineItems;
    this.lineItemsTypes = params.lineItemsTypes;
    this.subtotal = params.subtotal;
    this.note = params.note;
    this.businessInfo = params.businessInfo;
    this.customerInfo = params.customerInfo;
    this.paymentInfo = params.paymentInfo;
    this.deliveryInfo = params.deliveryInfo;
    this.checkoutId = params.checkoutId;
    this.coupon = params.coupon;
    this.purchaseFlowId = params.purchaseFlowId;
    this.hasSubscriptionItem = params.hasSubscriptionItem;
    this.hasItemWithFreeTrial = params.hasItemWithFreeTrial;
    this.taxInfo = params.taxInfo;
    this.hasFormsOnCheckout = params.hasFormsOnCheckout;
    this.hasLineItemWithCouponDiscount = params.hasLineItemWithCouponDiscount;
  }

  public static fromCartV1(cart: CartV1WithPrivateFields, modelContext: ModelContext): CartModel {
    const lineItemIdToRules = mapLineItemIdToRules(cart.appliedDiscounts as CartDiscountWithPrivateFields[]);
    const appliedCoupon = cart.appliedDiscounts?.find((discount) => discount?.coupon);

    const modeledLineItems = cart.lineItems.map((lineItem: LineItem) =>
      LineItemModel.fromCartV1(lineItem as LineItemWithPrivateFields, lineItemIdToRules[lineItem._id], modelContext)
    );

    return new CartModel({
      id: cart._id,
      lineItems: modeledLineItems,
      lineItemsTypes: modelContext.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings)
        ? getUniques(modeledLineItems.map(({attributes}) => attributes.itemType))
        : [],
      subtotal: PriceModel.fromCartV1(cart.subtotal, modelContext),
      note: cart.buyerNote,
      businessInfo: BusinessInfoModel.fromCartV1(cart.currency),
      customerInfo: CustomerInfoModel.fromCartV1(cart.conversionCurrency),
      paymentInfo: PaymentInfoModel.fromCartV1(cart.paymentCurrency, cart.contactInfo?.address),
      checkoutId: cart.checkoutId,
      coupon: appliedCoupon ? CouponModel.fromCartV1(appliedCoupon?.coupon) : undefined,
      purchaseFlowId: cart.purchaseFlowId,
      deliveryInfo: DeliveryInfoModel.fromCartV1(cart.contactInfo?.address),
      hasSubscriptionItem: cart.lineItems.some((lineItem) => lineItem.subscriptionOptionInfo),
      hasItemWithFreeTrial: cart.lineItems.some(
        (lineItem) => !!lineItem.subscriptionOptionInfo?.subscriptionSettings?.freeTrialPeriod
      ),
      taxInfo: TaxInfoModel.fromCartV1(cart.taxIncludedInPrices),
      hasLineItemWithCouponDiscount: undefined,
    });
  }

  public static fromCartV2(cart: Cart, modelContext: ModelContext): CartModel {
    const modeledLineItems = cart.lineItems.map((lineItem) => LineItemModel.fromCartV2(lineItem, modelContext));
    return new CartModel({
      id: cart._id,
      revision: cart.revision,
      lineItems: modeledLineItems,
      lineItemsTypes: modelContext.viewerContext.isExperimentEnabled(SPECS.GiftCardAddToCartSettings)
        ? getUniques(modeledLineItems.map(({attributes}) => attributes.itemType))
        : [],
      subtotal: PriceModel.fromCartV2(cart.subtotal, modelContext),
      note: cart.note,
      businessInfo: BusinessInfoModel.fromCartV2(cart.businessInfo),
      customerInfo: CustomerInfoModel.fromCartV2(cart.customerInfo),
      paymentInfo: PaymentInfoModel.fromCartV2(cart.paymentInfo),
      taxInfo: TaxInfoModel.fromCartV2(cart.taxInfo),
      coupon: cart.coupons?.[0] ? CouponModel.fromCartV2(cart.coupons?.[0]) : undefined,
      purchaseFlowId: cart.purchaseFlowId,
      deliveryInfo: DeliveryInfoModel.fromCartV2(cart.deliveryInfo),
      hasSubscriptionItem: cart.lineItems.some((lineItem) => lineItem.attributes.subscriptionInfo),
      hasItemWithFreeTrial: cart.lineItems.some(
        (lineItem) => !!lineItem.attributes.subscriptionInfo?.subscriptionSettings?.freeTrialPeriod
      ),
      hasFormsOnCheckout: modelContext.viewerContext.isExperimentEnabled(SPECS.ShowFormsPerLineItemsStep)
        ? cart.forms?.length > 0
        : undefined,
      hasLineItemWithCouponDiscount: modelContext.viewerContext.isExperimentEnabled(
        SPECS.CouponAlignmentInCartAndCheckout
      )
        ? true
        : undefined,
    });
  }
}

function mapLineItemIdToRules(appliedDiscounts: CartDiscountWithPrivateFields[]): Record<string, string[]> {
  if (!appliedDiscounts) {
    return {};
  }
  return appliedDiscounts.reduce((acc, discount) => {
    if (!discount.discountRule) {
      return acc;
    }
    discount.lineItemIds?.forEach((lineItemId) => {
      acc[lineItemId] = acc[lineItemId] ?? [];
      acc[lineItemId].push(discount.discountRule.name.translated);
    });
    return acc;
  }, {});
}
